import type { Brief, Propuesta } from "../tipos";

/**
 * TODOS los datos de ejemplo del Estudio de marketing, en un solo archivo.
 *
 * Aislados a propósito: el día que la sección se abra a clientes, tiene que ser
 * imposible que un número inventado se cuele como si fuera suyo. Ninguna
 * pantalla arma datos falsos por su cuenta.
 */

const AHORA = "2026-08-16T14:22:03Z";

const meta = (producido_por: string, prompt_version: string | null, modelo: string | null) => ({
  estado: "listo" as const,
  producido_por,
  modelo,
  prompt_version,
  cuando: AHORA,
});

export const PROPUESTAS_EJEMPLO: Propuesta[] = [
  {
    pieza_id: "pza_8f21",
    titulo: "Reel: campera oversize en la calle",
    estado: "listo",
    coordenada: {
      persona: "compra_por_calidad_30_45",
      angulo: "problema_solucion",
      formato: "reel_9x16",
      canal: "instagram",
    },
    por_que:
      "Tus reels con producto en movimiento retienen 3x más que las fotos de catálogo. La campera es tu top de ventas y no tiene contenido hace 18 días.",
    respaldo: [
      {
        metrica: "retencion_p75",
        valor: 0.41,
        comparado_con: 0.13,
        contra_que: "fotos de catálogo",
        ventana: "ultimos_30_dias",
        fuente: "instagram",
        fila_origen: "spm_44a1",
      },
      {
        metrica: "dias_sin_contenido",
        valor: 18,
        ventana: "desde_ultima_pieza",
        fuente: "atlas",
        fila_origen: "prod_9c02",
      },
    ],
    tipo: "explotacion",
    duda: null,
    confianza: 0.78,
    riesgo: "bajo",
    creditos_estimados: 18,
  },
  {
    pieza_id: "pza_31bd",
    titulo: "Anuncio: pack tres remeras",
    estado: "generando",
    coordenada: {
      persona: "compra_por_precio_20_28",
      angulo: "problema_solucion",
      formato: "feed_1x1",
      canal: "meta_ads",
    },
    por_que:
      "Tres de tus cuatro anuncios activos están en frecuencia alta: cierran bien pero ya casi no entra gente nueva. Esto es un ángulo nuevo para público frío.",
    respaldo: [
      {
        metrica: "frecuencia",
        valor: 4.1,
        ventana: "ultimos_7_dias",
        fuente: "meta_ads",
        fila_origen: "acm_77e3",
      },
    ],
    tipo: "exploracion",
    duda: null,
    confianza: 0.71,
    riesgo: "medio",
    creditos_estimados: 12,
  },
  {
    pieza_id: "pza_5c40",
    titulo: "Carrusel: guía de talles",
    estado: "propuesta",
    coordenada: {
      persona: "compra_por_calidad_30_45",
      angulo: "educativo",
      formato: "carrusel_4x5",
      canal: "instagram",
    },
    por_que:
      "Tus posts guardados son casi todos informativos, no de producto. Es el tipo de contenido que más se guarda en tu cuenta.",
    respaldo: [
      {
        metrica: "saves_relativo",
        valor: 4.2,
        comparado_con: 1,
        contra_que: "el promedio de tu cuenta",
        ventana: "ultimos_90_dias",
        fuente: "instagram",
        fila_origen: "spm_1207",
      },
    ],
    tipo: "exploracion",
    duda: "No puedo confirmar si funciona para venta directa: todavía no tengo tus datos de Tiendanube.",
    confianza: 0.55,
    riesgo: "bajo",
    creditos_estimados: 9,
  },
  {
    pieza_id: "pza_0a77",
    titulo: "Buzo rústico · carrusel",
    estado: "en_observacion",
    coordenada: {
      persona: "compra_por_precio_20_28",
      angulo: "detras_de_escena",
      formato: "carrusel_4x5",
      canal: "instagram",
    },
    por_que:
      "Lleva tres días al aire y las métricas todavía están erráticas. No propongo nada hasta que se asiente.",
    respaldo: [],
    tipo: "exploracion",
    duda: "Con tres días de datos no puedo distinguir un anuncio errático de uno malo.",
    confianza: 0.2,
    riesgo: "bajo",
    creditos_estimados: null,
  },
];

export const BRIEF_EJEMPLO: Brief = {
  pieza_id: "pza_8f21",
  version: 3,
  sale_de_version: 2,
  la_escribio: "usuario",
  cuando: AHORA,
  coordenada: PROPUESTAS_EJEMPLO[0].coordenada,
  estrategia: {
    _meta: meta("estratega", "est_v7", "claude-opus-5"),
    por_que: PROPUESTAS_EJEMPLO[0].por_que,
    respaldo: PROPUESTAS_EJEMPLO[0].respaldo,
    tipo: "explotacion",
    producto_id: "prod_9c02",
    confianza: 0.78,
    duda: null,
    riesgo: "bajo",
  },
  direccion_creativa: {
    _meta: meta("director_creativo", "dc_v4", "gemini-2.5-pro"),
    direccion_visual:
      "Calle al atardecer, luz cálida lateral, cámara a la altura del pecho. Paleta de la marca sobre fondos de hormigón.",
    reglas_de_marca_aplicadas: [
      "mirada_fuera_de_cuadro",
      "sin_degrade_salvo_descuento",
      "logo_abajo_derecha",
    ],
    avatar_id: null,
  },
  guion: {
    _meta: meta("director_creativo", "dc_v4", "gemini-2.5-pro"),
    hook: "la que te ponés arriba de todo",
    beats: [
      {
        n: 1,
        desde_s: 0,
        hasta_s: 3,
        texto_en_pantalla: "la que te ponés arriba de todo",
        voz: null,
        que_se_entiende: "una campera colgada, sola, en un ambiente urbano",
        densidad_subtitulo: 2,
      },
      {
        n: 2,
        desde_s: 3,
        hasta_s: 9,
        texto_en_pantalla: "oversize de verdad, no talle grande",
        voz: null,
        que_se_entiende: "alguien se la pone y le queda holgada de hombros",
        densidad_subtitulo: 5,
      },
      {
        n: 3,
        desde_s: 9,
        hasta_s: 15,
        texto_en_pantalla: "última tanda de la temporada",
        voz: null,
        que_se_entiende: "plano corto de la etiqueta y el logo",
        densidad_subtitulo: 4,
      },
    ],
    cta: "Link en bio",
  },
  plan_de_planos: {
    _meta: meta("director_ia", "dia_v2", "gemini-2.5-pro"),
    planos: [
      {
        id: "p1",
        beat: 1,
        tipo: "imagen_fija",
        descripcion: "campera colgada en pared de hormigón, luz lateral",
        referencia_de: ["p2"],
      },
      {
        id: "p2",
        beat: 1,
        tipo: "clip",
        descripcion: "misma campera, cámara acercándose lento",
        usa_referencia: "p1",
        lleva_voz: false,
      },
      {
        id: "p3",
        beat: 2,
        tipo: "clip",
        descripcion: "persona poniéndosela, plano medio",
        usa_referencia: null,
        lleva_voz: false,
      },
      {
        id: "p4",
        beat: 2,
        tipo: "imagen_fija",
        descripcion: "detalle de hombro, caída del género",
        referencia_de: [],
      },
      {
        id: "p5",
        beat: 3,
        tipo: "clip",
        descripcion: "giro corto mostrando la espalda",
        usa_referencia: null,
        lleva_voz: false,
      },
      {
        id: "p6",
        beat: 3,
        tipo: "imagen_fija",
        descripcion: "etiqueta y logo en primer plano",
        referencia_de: [],
      },
    ],
    total_planos: 6,
    con_movimiento: 3,
  },
  assets: {
    _meta: meta("director_ia", "dia_v2", "seedream"),
    grafo_id: "grf_2a91",
    resumen: [
      { plano: "p1", estado: "aprobado", intentos: 1, creditos: 3 },
      {
        plano: "p2",
        estado: "aprobado",
        intentos: 2,
        creditos: 6,
        rechazo: "anatomía: mano con seis dedos",
      },
      { plano: "p3", estado: "aprobado", intentos: 1, creditos: 3 },
      { plano: "p4", estado: "aprobado", intentos: 1, creditos: 2 },
      { plano: "p5", estado: "aprobado", intentos: 1, creditos: 3 },
      { plano: "p6", estado: "aprobado", intentos: 1, creditos: 1 },
    ],
    creditos_totales: 18,
  },
  armado: {
    _meta: meta("armado", null, null),
    duracion_s: 15,
    relacion: "9:16",
    subtitulos_quemados: true,
    musica: "sin_voz",
    area_segura_verificada: true,
  },
  copy_de_canal: {
    _meta: meta("copy_de_canal", "cc_v3", "gemini-2.5-flash"),
    instagram_feed: {
      caption:
        "Oversize de verdad, no talle grande. Última tanda de la temporada. Link en bio.",
      hashtags: ["#oversize", "#invierno"],
    },
  },
};

/** Métricas de resumen de la pantalla Plan. */
export const RESUMEN_EJEMPLO = [
  { clave: "Mejor formato", valor: "Video 9:16", delta: "3,1x vs foto", sentido: "sube" as const },
  { clave: "ROAS 30d", valor: "2,4x", delta: "+0,3", sentido: "sube" as const },
  { clave: "Público nuevo", valor: "1 de 4", delta: "solo un tractor", sentido: "baja" as const },
];

/** Bandas del embudo. `porcion_gasto` es lo que define el ANCHO de la banda. */
export const EMBUDO_EJEMPLO = {
  bandas: [
    {
      nombre: "Público nuevo",
      porcion_gasto: 0.46,
      slots_ocupados: 2,
      slots_objetivo: 3,
      anuncios: [
        { nombre: "Remeras · video calle", lectura: "Tractor", desplazamiento: null },
        { nombre: "Campera · UGC", lectura: "Tractor", desplazamiento: null },
      ],
    },
    {
      nombre: "Calentamiento",
      porcion_gasto: 0.12,
      slots_ocupados: 1,
      slots_objetivo: 1,
      anuncios: [
        {
          nombre: "Buzo · carrusel",
          lectura: "Cerrador",
          desplazamiento: "hace 9 días estaba arriba",
        },
      ],
    },
    {
      nombre: "Cierre",
      porcion_gasto: 0.42,
      slots_ocupados: 3,
      slots_objetivo: 2,
      anuncios: [
        { nombre: "Pack tres remeras", lectura: "Cerrador", desplazamiento: null },
        { nombre: "Descuento 20%", lectura: "Cerrador", desplazamiento: null },
        { nombre: "Envío gratis", lectura: "Cerrador", desplazamiento: null },
      ],
    },
  ],
  sin_asentar: [
    { nombre: "Pantalón cargo · foto", nota: "3 días" },
    { nombre: "Remeras · versión B", nota: "2 días" },
  ],
  no_suma: [{ nombre: "Campera · foto estudio", nota: "CPM $8.900" }],
  tarjetas: {
    guardados: [
      { nombre: "Guía de talles", valor: "4,2x", peso: 1 },
      { nombre: "Detrás de escena", valor: "2,8x", peso: 0.66 },
      { nombre: "Producto en fondo neutro", valor: "0,7x", peso: 0.17 },
    ],
    retencion: [
      { nombre: "Video con movimiento", valor: "41%", peso: 1 },
      { nombre: "Video estático", valor: "19%", peso: 0.46 },
      { nombre: "Carrusel", valor: "13%", peso: 0.31 },
    ],
    sin_contenido: [
      { nombre: "Campera oversize", valor: "18 días" },
      { nombre: "Buzo rústico", valor: "26 días" },
      { nombre: "Pantalón cargo", valor: "31 días" },
    ],
  },
};

export const ESTRATEGIA_EJEMPLO = {
  nombre: "Anillos olímpicos",
  procedencia: "verificada" as const,
  rango: "Aplica de $300 a $5.000 por mes",
  en_uso_dias: 34,
  slots: [
    { funcion: "Prospección 1", quien: "Remeras · video calle", estado: "Tractor · frec. 1,4", vacio: false },
    { funcion: "Prospección 2", quien: "Campera · UGC", estado: "Tractor · frec. 1,6", vacio: false },
    { funcion: "Prospección 3", quien: null, estado: "Falta desde hace 9 días", vacio: true },
    { funcion: "Cierre 1", quien: "Pack tres remeras", estado: "Cerrador · frec. 4,1", vacio: false },
    { funcion: "Cierre 2", quien: "Envío gratis", estado: "Cerrador · frec. 3,4", vacio: false },
  ],
  fuera_de_estructura: "1 anuncio de cierre extra corriendo",
  reglas: [
    { nombre: "Rotar", texto: "Solo si el slot cae bajo objetivo dos semanas seguidas" },
    { nombre: "Escalar", texto: "Si el CPA está bajo objetivo y el presupuesto se entrega, subir 20% y no tocar creativos" },
    { nombre: "Prohibido", texto: "Lanzar más de un anuncio nuevo por semana en la misma campaña" },
  ],
  por_que_funciona: [
    "Menos anuncios concentran la señal y la plataforma encuentra patrones antes",
    "Cada lanzamiento nuevo canibaliza alcance de los existentes: limitarlos evita el doom cycle",
    "Con más de $5.000 al mes conviene otra estructura",
  ],
};
